/**
 * Comprehensive Wiki Data for Staff Consultations
 * Quick access to all essential information during client calls
 */

export const wikiCategories = {
  countries: {
    id: 'countries',
    title: 'Countries & Programs',
    icon: '🌍',
    color: 'blue'
  },
  packages: {
    id: 'packages',
    title: 'Packages & Pricing',
    icon: '💰',
    color: 'green'
  },
  eligibility: {
    id: 'eligibility',
    title: 'Eligibility & Requirements',
    icon: '✅',
    color: 'purple'
  },
  financial: {
    id: 'financial',
    title: 'Financial Requirements',
    icon: '💳',
    color: 'orange'
  },
  process: {
    id: 'process',
    title: 'Process & Timelines',
    icon: '⏱️',
    color: 'indigo'
  },
  documents: {
    id: 'documents',
    title: 'Documents Checklist',
    icon: '📋',
    color: 'pink'
  },
  faq: {
    id: 'faq',
    title: 'FAQ & Common Queries',
    icon: '❓',
    color: 'yellow'
  },
  contact: {
    id: 'contact',
    title: 'Contact & Support',
    icon: '📞',
    color: 'red'
  },
  customEmail: {
    id: 'customEmail',
    title: 'Custom Email Creation',
    icon: '📧',
    color: 'cyan'
  }
};

export const wikiData = {
  countries: {
    germany: {
      name: 'Germany',
      flag: '🇩🇪',
      popularPrograms: [
        "Bachelor's Degree (3-4 years)",
        "Master's Degree (2 years)",
        'PhD Programs',
        'Chancenkarte (Job Seeker Visa)',
        'Ausbildung (Vocational Training)',
        'Au Pair Programs',
        'Language Visa'
      ],
      keyFeatures: [
        'Tuition-free public universities',
        '18-month job seeker visa after graduation',
        'World-class education with global recognition',
        'Strong economy with excellent job opportunities',
        'Central location in Europe'
      ],
      requirements: {
        bachelor: {
          academic: 'A/L 3 Passes',
          language: 'IELTS 6.0+ or German B1/B2',
          ieltsNote: 'Applicants without IELTS can apply for some programs',
          additional: ['APS Certificate', 'Blocked Account €13,080/year']
        },
        master: {
          academic: 'Recognized 4-year bachelor degree with 2.85+ GPA',
          language: 'IELTS 6.0-6.5 or German B2/C1',
          additional: ['APS Certificate', 'Blocked Account €13,080/year']
        },
        chancenkarte: {
          academic: 'Bachelor degree or recognized vocational qualification',
          language: 'German B1/B2 or English IELTS 6.0-6.5',
          workExp: '2+ years relevant work experience',
          additional: ['Points assessment', 'Proof of funds']
        }
      },
      costs: {
        tuition: 'Public: €250-500/semester | Private: €20,000-30,000/year',
        living: '€13,080/year (blocked account requirement)',
        visa: '€100-150',
        consulting: {
          diy: 'LKR 400,000',
          plus: 'LKR 850,000',
          pro: 'LKR 1,190,000'
        }
      },
      timeline: {
        bachelor: '3-4 months for university application + 2-3 months for visa',
        master: '3-4 months for university application + 2-3 months for visa',
        chancenkarte: '2-3 months total processing time'
      }
    },
    canada: {
      name: 'Canada',
      flag: '🇨🇦',
      popularPrograms: [
        'Certificate Programs (1 year)',
        'Diploma Programs (2 years)',
        'Post-Graduate Diploma',
        "Bachelor's Degree",
        "Master's Degree",
        'PhD Programs'
      ],
      keyFeatures: [
        'Study loan assistance up to 75% tuition',
        'Post-graduation work permit (8 months - 3 years)',
        'Pathway to permanent residency',
        'Diverse and welcoming culture',
        'High quality of life'
      ],
      requirements: {
        general: {
          academic: 'A/L completion (minimum requirements vary by program)',
          language: 'IELTS 6.0 overall (TOEFL, PTE accepted)',
          financial: 'Show money minimum LKR 5M'
        }
      },
      costs: {
        tuition: 'CAD 17,000-22,500/year',
        living: 'CAD 20,000-22,500/year',
        visa: 'CAD 150-250',
        flight: 'CAD 1,200-1,800',
        consulting: {
          standard: 'LKR 1,150,000'
        }
      },
      financialSupport: {
        coverage: 'Up to 75% of tuition',
        repayment: '4-7 years',
        interest: '11% APR',
        monthlyPayment: 'CAD 398-790'
      },
      timeline: '3-4 months total processing time'
    },
    australia: {
      name: 'Australia',
      flag: '🇦🇺',
      popularPrograms: [
        "Bachelor's Degree (3 years)",
        "Master's Degree (1.5-2 years)",
        'Top-up Degrees',
        'Foundation Programs'
      ],
      keyFeatures: [
        'Post-study work rights 2-4 years',
        'Pathway to permanent residency',
        'Part-time work during studies (40 hours/fortnight)',
        'High-quality education system',
        'Excellent climate and lifestyle'
      ],
      requirements: {
        bachelor: {
          academic: 'A/L with minimum 2-3 passes',
          language: 'IELTS 6.5 overall',
          financial: 'Show money minimum LKR 5M'
        },
        master: {
          academic: 'Recognized bachelor degree with competitive GPA',
          language: 'IELTS 6.5 overall',
          financial: 'Show money minimum LKR 5M'
        }
      },
      costs: {
        tuition: 'AUD 20,000-45,000/year',
        living: 'AUD 21,041/year',
        visa: 'AUD 650',
        oshc: 'AUD 500-700/year (mandatory health insurance)',
        consulting: {
          pro: 'LKR 1,190,000'
        }
      },
      timeline: '3-4 months total processing time'
    },
    uk: {
      name: 'United Kingdom',
      flag: '🇬🇧',
      popularPrograms: [
        "Bachelor's Degree (3 years)",
        "Master's Degree (1 year)",
        'Foundation Programs (6-12 months)',
        'PhD Programs'
      ],
      keyFeatures: [
        '1-year Master programs',
        '2-year Graduate Route work permit',
        'Rich history and culture',
        'Globally recognized universities',
        'Diverse student population'
      ],
      requirements: {
        foundation: {
          academic: 'O/L or A/L completion (flexible grades)',
          language: 'IELTS 5.5'
        },
        bachelor: {
          academic: 'A/L with minimum 2-3 passes',
          language: 'IELTS 5.5 overall'
        },
        master: {
          academic: 'Recognized bachelor degree with competitive GPA',
          language: 'IELTS 6.0 overall (MOI accepted by many)'
        }
      },
      costs: {
        tuition: {
          bachelor: '£10,000-20,000/year',
          master: '£11,000-22,000 total',
          foundation: '£8,000-15,000'
        },
        living: {
          outsideLondon: '£9,000-12,000/year',
          london: '£12,000-15,000/year'
        },
        visa: '£450',
        nhs: '£470/year (health surcharge)',
        consulting: 'LKR 400,000'
      },
      timeline: '2-3 months total processing time'
    },
    newzealand: {
      name: 'New Zealand',
      flag: '🇳🇿',
      popularPrograms: [
        "Bachelor's Degree (3 years)",
        "Master's Degree (1-2 years)",
        'Work Visa Programs',
        'Skilled Migration'
      ],
      keyFeatures: [
        'Study-to-residency migration support',
        'Post-study work and residency pathways',
        'Beautiful environment and quality of life',
        'Work-life balance opportunities',
        'Support through entire migration journey'
      ],
      requirements: {
        general: {
          academic: 'A/L completion (varies by program)',
          language: 'IELTS 6.0-6.5',
          financial: 'Show money minimum LKR 5M'
        }
      },
      costs: {
        consulting: 'LKR 1,190,000',
        note: 'Contact for specific tuition and living costs'
      },
      timeline: 'Contact for current processing times'
    }
  },
  packages: {
    diy: {
      name: 'DIY Package',
      price: 'LKR 400,000',
      refundable: 'No (Non-refundable)',
      description: 'Self-managed pathway with expert checks and guidance when you need it.',
      features: [
        'Document verification for all 5 countries',
        'SOP/CV/LoR feedback & formatting support',
        'Comprehensive eligibility assessment',
        'Client portal access with status tracking',
        'Email support during business hours'
      ],
      idealFor: 'Self-motivated students who want guidance while managing their own process',
      countries: 'All 5 countries covered',
      paymentPlan: {
        upfront: 'LKR 400,000',
        afterOffer: 'LKR 0',
        final: 'LKR 0'
      }
    },
    plus: {
      name: 'Essential Package',
      price: 'LKR 850,000',
      refundable: 'Yes (Partially Refundable)',
      description: 'Smart choice for qualified students who want end-to-end handling for one country.',
      features: [
        'Everything in DIY, plus:',
        '3 German public university applications managed for you',
        'Visa documentation review & appointment scheduling',
        'Blocked account and public health insurance guidance',
        'Document review with prioritized feedback',
        'LKR 25,000 referral commission per successful referral'
      ],
      idealFor: 'Budget-conscious students ready with documents who want guided delivery',
      countries: 'One country (full-service delivery)',
      paymentPlan: {
        upfront: 'LKR 425,000 (50%)',
        afterOffer: 'LKR 425,000 (50%)',
        final: 'LKR 0'
      }
    },
    pro: {
      name: 'Premium Package',
      price: 'LKR 1,190,000',
      refundable: 'Yes (Partially Refundable)',
      description: 'Complete peace of mind with success guarantee, multiple applications, and premium benefits.',
      features: [
        'Everything in Essential, plus:',
        'Apply to ALL 5 countries with one profile',
        '5+ university applications (3 public + 2 private backup)',
        'Application Success Guarantee (free re-application next semester)',
        'Dedicated consultant with priority support line',
        'Free IELTS prep course + first month travel insurance',
        'Student & alumni network with 25% family visa discount',
        'LKR 50,000 referral commission per successful referral',
        '100GB cloud storage + dedicated email + 1 year Google Gemini Pro access'
      ],
      idealFor: 'Families and professionals seeking the safest all-inclusive pathway with backups',
      countries: 'All 5 countries (full-service delivery)',
      paymentPlan: {
        upfront: 'LKR 595,000 (50%)',
        afterOffer: 'LKR 595,000 (50%)',
        final: 'LKR 0'
      }
    }
  },
  eligibility: {
    commonRequirements: [
      'A/L 3 Passes (any stream)',
      "Bachelor's Degree or Equivalent Qualification",
      'IELTS 6.0 (Applicants who haven\'t taken the test can still apply)',
      'Show Money / Blocked Account: Minimum of LKR 5 Million for proof of funds'
    ],
    byCountry: {
      germany: {
        bachelor: [
          'A/L 3 Passes minimum',
          'IELTS 6.0+ or German B1/B2',
          'APS Certificate required',
          'Blocked Account €13,080/year'
        ],
        master: [
          'Recognized 4-year bachelor degree',
          'Minimum 2.85 GPA',
          'IELTS 6.0-6.5 or German B2/C1',
          'APS Certificate required',
          'Blocked Account €13,080/year'
        ],
        chancenkarte: [
          'Bachelor degree or recognized qualification',
          '2+ years relevant work experience',
          'German B1/B2 or English IELTS 6.0-6.5',
          'Points assessment',
          'Proof of funds'
        ]
      },
      canada: [
        'A/L completion',
        'IELTS 6.0 overall (TOEFL, PTE accepted)',
        'Show money minimum LKR 5M',
        'Program-specific prerequisites may apply'
      ],
      australia: {
        bachelor: [
          'A/L with minimum 2-3 passes',
          'IELTS 6.5 overall',
          'Show money minimum LKR 5M'
        ],
        master: [
          'Recognized bachelor degree',
          'Competitive GPA required',
          'IELTS 6.5 overall',
          'Show money minimum LKR 5M'
        ]
      },
      uk: {
        foundation: [
          'O/L or A/L completion',
          'Flexible grade requirements',
          'IELTS 5.5'
        ],
        bachelor: [
          'A/L with minimum 2-3 passes',
          'IELTS 5.5 overall'
        ],
        master: [
          'Recognized bachelor degree',
          'Competitive GPA required',
          'IELTS 6.0 overall (MOI accepted by many)'
        ]
      },
      newzealand: [
        'A/L completion',
        'IELTS 6.0-6.5',
        'Show money minimum LKR 5M',
        'Program-specific requirements'
      ]
    }
  },
  financial: {
    showMoney: {
      minimum: 'LKR 5,000,000',
      purpose: 'Proof of funds for visa application',
      note: 'Must be in bank account for 6+ months (typically)'
    },
    blockedAccount: {
      germany: {
        amount: '€13,080/year',
        lkr: 'LKR 4,470,000/year',
        purpose: 'Covers first 12 months living expenses',
        note: 'Can be replaced by German sponsorship in some cases'
      }
    },
    studyLoans: {
      canada: {
        coverage: 'Up to 75% of tuition',
        repayment: '4-7 years',
        interestRate: '11% APR',
        monthlyPayment: 'CAD 398-790'
      }
    },
    consultingFees: {
      diy: 'LKR 400,000 (All countries, self-managed)',
      uk: 'LKR 400,000 (Single country)',
      plus: 'LKR 850,000 (One country, full-service)',
      canada: 'LKR 1,150,000',
      pro: 'LKR 1,190,000 (All countries, full-service)',
      chancenkarte: 'LKR 1,390,000'
    },
    paymentPlans: {
      diy: {
        upfront: '100%',
        installments: 'No'
      },
      plus: {
        upfront: '50%',
        afterOffer: '50%',
        installments: '2'
      },
      pro: {
        upfront: '50%',
        afterOffer: '50%',
        installments: '2'
      },
      canada: {
        upfront: '25%',
        afterOffer: '75% financed',
        note: 'Loan after arrival'
      }
    }
  },
  process: {
    general: {
      steps: [
        'Initial consultation and profile assessment',
        'Package selection and contract signing',
        'Document collection and verification',
        'University/college application submission',
        'Offer letter receipt',
        'Visa application preparation and submission',
        'Visa approval and payment of balance',
        'Pre-departure orientation',
        'Departure and arrival support'
      ],
      totalTimeline: '3-6 months (varies by country and program)'
    },
    byCountry: {
      germany: {
        steps: [
          'APS Certificate application (if required)',
          'University application via Uni-Assist',
          'Receive admission letter',
          'Blocked account setup',
          'Visa appointment booking',
          'Visa interview and submission',
          'Visa processing (2-3 months)',
          'Arrival in Germany'
        ],
        timeline: '4-6 months total',
        criticalPath: 'APS Certificate (2-4 weeks) → University (2-4 months) → Visa (2-3 months)'
      },
      canada: {
        steps: [
          'College/university application',
          'Receive offer letter',
          'Pay deposit (25%)',
          'Study permit application',
          'Medical exam (if required)',
          'Biometrics appointment',
          'Receive study permit',
          'Loan processing (if applicable)',
          'Arrival in Canada'
        ],
        timeline: '3-4 months total',
        criticalPath: 'Application (1-2 months) → Study permit (2-3 months)'
      },
      uk: {
        steps: [
          'UCAS/application submission',
          'Receive offer letter',
          'CAS (Confirmation of Acceptance) issuance',
          'Pay initial fee (if required)',
          'Tier 4 visa application',
          'Biometrics appointment',
          'Visa decision',
          'Arrival in UK'
        ],
        timeline: '2-3 months total',
        criticalPath: 'University (1-2 months) → Visa (2-3 weeks)'
      },
      australia: {
        steps: [
          'University application',
          'Receive offer letter',
          'Accept offer and pay deposit',
          'CoE (Confirmation of Enrolment) issuance',
          'Student visa subclass 500 application',
          'Health insurance (OSHC) purchase',
          'Visa decision',
          'Arrival in Australia'
        ],
        timeline: '3-4 months total',
        criticalPath: 'University (1-2 months) → Visa (1-2 months)'
      }
    }
  },
  documents: {
    general: [
      'Valid Passport (6+ months validity)',
      'Passport-size photographs',
      'Birth certificate',
      'Academic certificates (O/L, A/L, Degree)',
      'Academic transcripts',
      'IELTS/TOEFL/PTE certificate (if available)',
      'CV/Resume',
      'Statement of Purpose (SOP)',
      'Recommendation letters (2-3)',
      'Experience certificates (if applicable)',
      'Bank statements (6+ months)',
      'Bank balance certificate',
      'Show money certificate',
      'Sponsorship documents (if applicable)',
      'Medical reports (if required)',
      'Police clearance certificate (if required)'
    ],
    germanySpecific: [
      'APS Certificate',
      'Uni-Assist registration and application',
      'Blocked account confirmation (Fintiba/Spencer)',
      'Health insurance confirmation',
      'Accommodation proof',
      'cv.ukebescheinigung (educational leave certificate - if employer-sponsored)'
    ],
    canadaSpecific: [
      'Letter of Acceptance from DLI',
      'Provincial Health Card (if applicable)',
      'Provincial health insurance documentation',
      'GIC (Guaranteed Investment Certificate) - some cases',
      'Loan approval letter (if applicable)'
    ],
    ukSpecific: [
      'CAS (Confirmation of Acceptance for Studies)',
      'ATAS certificate (if applicable)',
      'Academic Technology Approval Scheme',
      'Tuberculosis test results (if from specified countries)',
      'Parental consent (if under 18)'
    ],
    australiaSpecific: [
      'CoE (Confirmation of Enrolment)',
      'OSHC (Overseas Student Health Cover)',
      'GTE (Genuine Temporary Entrant) statement',
      'English proficiency test results',
      'Financial capacity evidence'
    ],
    newzealandSpecific: [
      'Letter of Acceptance from NZ institution',
      'Medical certificates',
      'Police clearance certificates',
      'Financial supporting documents'
    ]
  },
  faq: [
    {
      question: 'What are the minimum A/L requirements?',
      answer: 'Generally, 3 A/L passes are required. Different countries have different requirements: Germany, Canada, UK, Australia, and New Zealand all accept 3 passes minimum for bachelor\'s programs. Some foundation programs in the UK accept O/L results as well.',
      category: 'eligibility'
    },
    {
      question: 'Can I apply without IELTS?',
      answer: 'Yes! Many programs accept applications without IELTS. You can take the test later or apply for programs that accept other forms of English proficiency proof (Medium of Instruction letters). However, having IELTS 6.0+ strengthens your application significantly.',
      category: 'eligibility'
    },
    {
      question: 'What is Show Money and how much do I need?',
      answer: 'Show Money is proof that you have sufficient funds to support your studies. The minimum requirement is LKR 5,000,000 in your bank account for at least 6 months. Some countries like Germany require a blocked account with €13,080/year.',
      category: 'financial'
    },
    {
      question: 'Which package should I choose?',
      answer: 'DIY (LKR 400K): Self-managed for all 5 countries. Essential (LKR 850K): Full-service for one country. Premium (LKR 1,190K): Full-service for all 5 countries with success guarantee. Choose based on your budget, time availability, and risk tolerance.',
      category: 'packages'
    },
    {
      question: 'How long does the process take?',
      answer: 'Germany: 4-6 months | Canada: 3-4 months | UK: 2-3 months | Australia: 3-4 months | New Zealand: Contact for details. Timeline includes university application + visa processing.',
      category: 'process'
    },
    {
      question: 'Can I work while studying?',
      answer: 'Yes! Germany: 120 full days or 240 half days per year | Canada: Up to 20 hours/week during studies | Australia: 40 hours per fortnight | UK: Up to 20 hours/week | New Zealand: Varies by visa type.',
      category: 'countries'
    },
    {
      question: 'What are the post-graduation work opportunities?',
      answer: 'Germany: 18-month job seeker visa | Canada: Up to 3-year work permit | UK: 2-year Graduate Route | Australia: 2-4 years | New Zealand: Various post-study work options.',
      category: 'countries'
    },
    {
      question: 'Are there scholarship opportunities?',
      answer: 'Limited scholarships for Sri Lankan students in public universities. Germany offersDAAD scholarships, UK hasChevening/Commonwealth scholarships, Australia has Australia Awards. Most rely on self-funding with part-time work support.',
      category: 'financial'
    },
    {
      question: 'What if my visa gets rejected?',
      answer: 'For PRO package: Free re-application next semester. For other packages: Re-application requires additional fees. Common rejection reasons: insufficient funds, weak SOP, incomplete documents, or interview performance. We provide detailed rejection analysis.',
      category: 'process'
    },
    {
      question: 'Can I bring my family?',
      answer: 'Yes, spouse and dependent visas available for all countries. PRO package includes 25% family visa discount. Family members can work in most countries (restrictions apply). Contact for specific family immigration requirements.',
      category: 'countries'
    },
    {
      question: 'What is the Flight Plan™ Alliance?',
      answer: 'Flight Plan™ is an alliance that provides standardised migration services in Sri Lanka that is AI powered. Ravana Migration is a proud alliance partner. Our unique service allows you to apply to all 5 countries (Germany, Canada, Australia, UK, New Zealand) with one comprehensive profile. Increases your chances of success and gives you multiple options to choose from.',
      category: 'packages'
    },
    {
      question: 'Do you help with accommodation?',
      answer: 'Yes! We assist with accommodation arrangements in all countries. This includes university dorms, shared apartments, and private housing. Some packages include pre-arranged accommodation support.',
      category: 'process'
    }
  ],
  contact: {
    offices: [
      {
        country: 'Sri Lanka',
        flag: '🇱🇰',
        city: 'Colombo',
        role: 'Headquarters',
        address: 'No 10/30/1/1, Pamankada Lane, Wellawatta, Colombo 06',
        email: 'info@ravanamigration.com',
        phone: '+94 11 XXX XXXX'
      },
      {
        country: 'Germany',
        flag: '🇩🇪',
        city: 'Multiple Cities',
        role: 'European Operations',
        email: 'germany@ravanamigration.com'
      },
      {
        country: 'Canada',
        flag: '🇨🇦',
        city: 'Multiple Cities',
        role: 'North American Operations',
        email: 'canada@ravanamigration.com'
      }
    ],
    support: {
      hours: 'Monday - Friday: 9:00 AM - 6:00 PM (Sri Lanka Time)',
      emergency: 'For urgent visa matters outside business hours',
      responseTime: 'Email: 24-48 hours | Phone: Same day'
    },
    departments: [
      { name: 'General Inquiries', email: 'info@ravanamigration.com' },
      { name: 'Visa Applications', email: 'visa@ravanamigration.com' },
      { name: 'University Applications', email: 'education@ravanamigration.com' },
      { name: 'Finance & Payments', email: 'finance@ravanamigration.com' },
      { name: 'Technical Support', email: 'support@ravanamigration.com' }
    ]
  },
  customEmail: {
    title: 'Custom Email Creation Guide',
    description: 'Step-by-step guide to create custom email accounts for students (firstname.initial@edumail.lk)',
    loginUrl: 'https://register.lk',
    credentials: {
      username: 'assets@ravanaindustries.com',
      password: 'Ravana@indust12'
    },
    defaultPassword: 'Ravana@2025',
    emailDomain: '@edumail.lk',
    steps: [
      {
        step: 1,
        title: 'Access Register.lk',
        description: 'Go to the hosting control panel',
        action: 'Click the button below to open Register.lk in a new tab',
        hasButton: true,
        buttonText: 'Open Register.lk',
        buttonUrl: 'https://register.lk'
      },
      {
        step: 2,
        title: 'Login to My Account',
        description: 'Click "My Account" at the top right corner',
        action: 'Use the following credentials:',
        credentials: {
          username: 'assets@ravanaindustries.com',
          password: 'Ravana@indust12'
        }
      },
      {
        step: 3,
        title: 'Navigate to Services',
        description: 'After logging in, click on "Services" from the menu',
        action: 'Look for the Services link in the navigation'
      },
      {
        step: 4,
        title: 'Activate Hosting Service',
        description: 'Find the hosting plan in your services list',
        action: 'Click the "Active" button on "Unmetered Hosting One Plus - SSDG1 - SD - L1"'
      },
      {
        step: 5,
        title: 'Login to cPanel',
        description: 'Access the control panel for email management',
        action: 'Click on "Login to cPanel" button'
      },
      {
        step: 6,
        title: 'Open Email Accounts',
        description: 'Navigate to the email section in cPanel',
        action: 'Look for and click "Email Accounts"'
      },
      {
        step: 7,
        title: 'Create New Email',
        description: 'Start creating a new email account',
        action: 'Click the "Create" button'
      },
      {
        step: 8,
        title: 'Enter Email Details',
        description: 'Fill in the student email information',
        action: 'Enter username in format: firstname.initial (e.g., john.s)',
        note: 'The email will be firstname.initial@edumail.lk'
      },
      {
        step: 9,
        title: 'Set Password',
        description: 'Set the default password for the new account',
        action: 'Enter password: Ravana@2025',
        password: 'Ravana@2025'
      },
      {
        step: 10,
        title: 'Finalize and Update System',
        description: 'Complete the email creation and update records',
        action: 'Click "Create" to finish, then update the student\'s credentials in the system'
      }
    ],
    notes: [
      'Email format: firstname.initial@edumail.lk (e.g., john.s@edumail.lk)',
      'Default password for all new accounts: Ravana@2025',
      'Remind students to change their password after first login',
      'Update the system with the created credentials after completion'
    ]
  }
};

// Search function for wiki content
export const searchWiki = (query) => {
  if (!query || query.trim() === '') return [];

  const searchTerm = query.toLowerCase().trim();
  const results = [];

  // Search through all wiki sections
  Object.keys(wikiData).forEach(sectionKey => {
    const section = wikiData[sectionKey];

    // Search countries
    if (sectionKey === 'countries') {
      Object.keys(section).forEach(countryKey => {
        const country = section[countryKey];

        // Check country name
        if (country.name.toLowerCase().includes(searchTerm)) {
          results.push({
            type: 'country',
            section: 'Countries',
            title: country.name,
            icon: country.flag,
            content: country.keyFeatures.slice(0, 2).join(', '),
            countryCode: countryKey
          });
        }

        // Check programs
        country.popularPrograms?.forEach(program => {
          if (program.toLowerCase().includes(searchTerm)) {
            results.push({
              type: 'program',
              section: 'Countries',
              title: `${country.flag} ${program}`,
              icon: '🎓',
              content: country.name,
              countryCode: countryKey
            });
          }
        });
      });
    }

    // Search packages
    if (sectionKey === 'packages') {
      Object.keys(section).forEach(packageKey => {
        const pkg = section[packageKey];
        if (pkg.name.toLowerCase().includes(searchTerm) ||
          pkg.description.toLowerCase().includes(searchTerm)) {
          results.push({
            type: 'package',
            section: 'Packages',
            title: pkg.name,
            icon: '💰',
            content: pkg.description,
            packageCode: packageKey
          });
        }
      });
    }

    // Search FAQ
    if (sectionKey === 'faq') {
      section.forEach(faq => {
        if (faq.question.toLowerCase().includes(searchTerm) ||
          faq.answer.toLowerCase().includes(searchTerm)) {
          results.push({
            type: 'faq',
            section: 'FAQ',
            title: faq.question,
            icon: '❓',
            content: faq.answer.substring(0, 100) + '...',
            category: faq.category
          });
        }
      });
    }

    // Search other sections
    Object.keys(section).forEach(key => {
      if (typeof section[key] === 'string' && section[key].toLowerCase().includes(searchTerm)) {
        results.push({
          type: sectionKey,
          section: sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1),
          title: key,
          icon: wikiCategories[sectionKey]?.icon || '📄',
          content: section[key]
        });
      }
    });
  });

  return results.slice(0, 20); // Limit to 20 results
};

export default wikiData;